import React from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableHead, TableRow } from '@material-ui/core'
import {
  checkForInvalid,
  createRequirement,
  EnergyType,
  Quadrant,
} from '../../../calculators/EnergyFlow'
import { energyPerYearUnit, powerUnit } from '../../../util/formatters'
import { Provider } from '../../../calculators/providers/common/Provider'
import { ReportDualEnergyAndPowerRow } from './ReportDualEnergyAndPowerRow'
import { TableHeaderUnitCell } from './TableHeaderUnitCell'
import { TableHeaderTitleCell } from './TableHeaderTitleCell'
import {
  electricityPurchasingCoolingProviderTypes,
  electricityPurchasingHeatingProviderTypes,
  electricityPurchasingProviderTypes,
} from '../../../provider/provider-properties'
import { useReportStyles } from './report-tab-styles'
import {
  HybridHeatPumpProvider,
  isHybridHeatPumpProvider,
} from '../../../calculators/providers/HybridHeatPumpProvider'
import { ProviderType } from '../../../generated/graphql'

type Props = {
  providers: Provider[]
}

type ElectricityPurchasingEnergyType = Exclude<
  EnergyType,
  EnergyType.Electricity
>

export function ReportProvidersPurchasedElectricityTable({
  providers,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useReportStyles()

  const electricityPurchasingProviders = providers.filter((provider) =>
    electricityPurchasingProviderTypes.includes(provider.type),
  )

  const filterProviders = (
    providersForRow: Provider[],
    type: ElectricityPurchasingEnergyType,
  ) => {
    if (type === EnergyType.Heating) {
      return providersForRow.filter((provider) =>
        electricityPurchasingHeatingProviderTypes.includes(provider.type),
      )
    }

    return providersForRow.filter(
      (provider) =>
        // Always include hybrid heat pump as we can determine required electricity for both heating and cooling.
        provider.type === ProviderType.HybridHeatPump ||
        (electricityPurchasingCoolingProviderTypes.includes(provider.type) &&
          // Do not include heating providers here although they also provide cooling
          !electricityPurchasingHeatingProviderTypes.includes(provider.type)),
    )
  }

  const patchHybridHeatPumpElectricityRequirements = (
    provider: HybridHeatPumpProvider,
    type: ElectricityPurchasingEnergyType,
  ) => {
    if (type === EnergyType.Heating) {
      // Instead of directly returning Requirement/Electricity, return required heating electricity.
      return createRequirement(
        EnergyType.Electricity,
        EnergyType.Electricity,
        provider.extraOutputValues.electricityRequiredByHeating,
      )
    }

    // Instead of directly returning Requirement/Electricity, return required cooling electricity.
    return createRequirement(
      EnergyType.Electricity,
      EnergyType.Electricity,
      provider.extraOutputValues.electricityRequiredByCooling,
    )
  }

  const getRequirementValuesFromProviders = (
    providersForRow: Provider[],
    type: ElectricityPurchasingEnergyType,
  ) =>
    filterProviders(providersForRow, type)
      .flatMap((provider) =>
        isHybridHeatPumpProvider(provider)
          ? patchHybridHeatPumpElectricityRequirements(provider, type)
          : provider.flows,
      )
      .filter(checkForInvalid)
      .filter((flow) => flow.quadrant === Quadrant.Requirement)
      .map((flow) => flow.hourSeries)

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableHeaderTitleCell
            title={t('report.purchasedElectricityTableHeader')}
          />
          <TableHeaderUnitCell
            firstRow={t('report.purchasedElectricityTableHeatingHeader')}
            secondRow={energyPerYearUnit}
          />
          <TableHeaderUnitCell
            firstRow={t('report.purchasedElectricityTableHeatingHeader')}
            secondRow={powerUnit}
          />
          <TableHeaderUnitCell
            firstRow={t('report.purchasedElectricityTableCoolingHeader')}
            secondRow={energyPerYearUnit}
          />
          <TableHeaderUnitCell
            firstRow={t('report.purchasedElectricityTableCoolingHeader')}
            secondRow={powerUnit}
          />
        </TableRow>
      </TableHead>
      <TableBody>
        {electricityPurchasingProviders.map((provider) => (
          <ReportDualEnergyAndPowerRow
            key={`providers-purchased-electricity-${provider.type}-${provider.id}`}
            title={provider.name}
            firstResultValues={getRequirementValuesFromProviders(
              [provider],
              EnergyType.Heating,
            )}
            secondResultValues={getRequirementValuesFromProviders(
              [provider],
              EnergyType.Cooling,
            )}
          />
        ))}

        <ReportDualEnergyAndPowerRow
          title={t('common.total')}
          firstResultValues={getRequirementValuesFromProviders(
            electricityPurchasingProviders,
            EnergyType.Heating,
          )}
          secondResultValues={getRequirementValuesFromProviders(
            electricityPurchasingProviders,
            EnergyType.Cooling,
          )}
          highlightRow
        />
      </TableBody>
    </Table>
  )
}
